/* eslint-disable react/require-default-props */
/* eslint-disable indent */
import { useWindowSize } from '@website/hooks/useWindowsSize';
import { UserDashboardQuery } from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import BookingTripCard from './me/bookings/BookingTripCard';
import BookingTripCardSmaller from './me/bookings/BookingTripCardSmaller';

/**
 *
 * linkToIndividualBookingPage
 *
 * If user is the booking owner, redirect to the individual booking page
 * otherwise redirect to the guest individual booking page
 *
 */
export function linkToIndividualBookingPage(
  userID: string,
  booking: BookingOrGuest,
) {
  const currentGuestSpaces = booking.spaces.filter(
    (space) => space.guest?.userID === userID,
  );
  // There are some instances where the guest is undefined, like when a booking
  // is cancelled, in which case we don't want to redirect to the guest's but
  // rather to the user's booking page
  if (currentGuestSpaces.length === 0) {
    return `/me/${userID}/bookings/${booking.id}`;
  }
  // booking owner doesn't change
  if (userID === currentGuestSpaces[0].guest?.bookingOwnerID) {
    return `/me/${userID}/bookings/${booking.id}`;
  }
  return `/me/${userID}/bookings/${booking.id}/guest/${currentGuestSpaces[0]?.id}`;
}

export function reviewTripLink(
  userID: string,
  booking: BookingOrGuest,
): string | undefined {
  const currentGuestSpaces = booking.spaces.filter(
    // we want to show review link if the user is a guest in the booking or if
    // booking owner and the guest hasn't been set
    (space) =>
      space.guest?.userID === userID ||
      // booking owner and guest hasn't been set, show review link
      (space.guest?.bookingOwnerID === userID && !space.guest?.userID),
  );
  // in case the current user is not a guest in the booking, don't show reviews link
  if (currentGuestSpaces.length === 0) {
    return undefined;
  }
  const pendingReviews = currentGuestSpaces.filter((space) => {
    if (space.guest?.review?.id) {
      return false;
    }
    return true;
  });
  const alreadyReviewed = currentGuestSpaces.filter((space) => {
    if (space.guest?.review?.id) {
      return true;
    }
    return false;
  });
  if (pendingReviews.length > 0 && alreadyReviewed.length === 0) {
    return `/me/${userID}/reviews/${pendingReviews[0].id}`;
  }
  // link to the review
  return undefined;
}

export function getReviewLink(
  userID: string,
  booking: BookingOrGuest,
): string | undefined {
  const currentGuestSpaces = booking.spaces.filter(
    (space) => space.guest?.userID === userID,
  );
  // in case the current user is not a guest in the booking, don't show reviews link
  if (currentGuestSpaces.length === 0) {
    return undefined;
  }
  const alreadyReviewed = currentGuestSpaces.filter((space) => {
    if (space.guest?.review?.id) {
      return true;
    }
    return false;
  });
  if (alreadyReviewed.length === 0) {
    return undefined;
  }
  return `/me/${userID}/reviews/${alreadyReviewed[0].guest?.review?.id}`;
}

type UserBookingPartial = UserDashboardQuery['user']['bookings']['items'][0];

export type BookingOrGuest = Omit<UserBookingPartial, 'user'>;

type BookingCardProps = {
  booking: BookingOrGuest;
  active?: boolean;
  badgeText?: string;
  userID: string;
  type?: 'small' | 'large';
};

/**
 * Booking Card
 * @param {Object} trip Can take {...trip} or individual parameters
 */
export default function BookingCard({
  booking,
  active = true,
  badgeText,
  userID,
  type = 'large',
}: BookingCardProps) {
  const { trip, group } = booking;

  const { width } = useWindowSize();

  // Generate link based on if booking owner or guest
  const linkToBookingDashboard = useMemo(
    () => linkToIndividualBookingPage(userID, booking),
    [userID, booking],
  );

  const reviewLink = useMemo(
    () => reviewTripLink(userID, booking),
    [userID, booking],
  );

  const reviewLinkAlreadyReviewed = useMemo(
    () => getReviewLink(userID, booking),
    [userID, booking],
  );

  const isTripDeparted =
    DateTime.fromISO(group?.start.utc, { zone: 'utc' }).diffNow().as('days') <
    0;

  return type === 'large' || width < 800 ? (
    <BookingTripCard
      title={trip.title}
      image={trip.image.file}
      subText1={`${DateTime.fromISO(booking.group.start.utc, {
        zone: booking.group.start.zone,
      }).toLocaleString(DateTime.DATETIME_MED)}`}
      link={linkToBookingDashboard}
      active={active}
      badgeText={badgeText}
      leaveReviewLink={reviewLink || reviewLinkAlreadyReviewed}
      reviewLinkText={
        reviewLink ? 'Leave a review for the trip' : 'View review'
      }
      isTripDeparted={isTripDeparted}
    />
  ) : (
    <BookingTripCardSmaller
      title={trip.title}
      image={trip.image.file}
      subText1={`${DateTime.fromISO(booking.group.start.utc, {
        zone: booking.group.start.zone,
      }).toLocaleString(DateTime.DATETIME_MED)}`}
      link={linkToBookingDashboard}
      active={active}
      badgeText={badgeText}
      leaveReviewLink={reviewLink}
      linkToReview={reviewLinkAlreadyReviewed}
      reviewLinkText={
        reviewLink ? 'Leave a review for the trip' : 'View review'
      }
      isTripDeparted={isTripDeparted}
    />
  );
}

type BookingCardsProps = {
  bookings: Array<BookingOrGuest>;
  active?: boolean;
  badgeText?: string;
  testId?: string;
  userID: string;
  size?: 'small' | 'large';
};

export function BookingCards({
  bookings,
  active = true,
  badgeText,
  testId,
  userID,
  size = 'large',
}: BookingCardsProps) {
  const items = bookings.map((item) => {
    return (
      <Col xs={12} sm={12} md={12} lg={12} className="px-2 py-1" key={item.id}>
        <BookingCard
          booking={item}
          active={active}
          badgeText={badgeText}
          userID={userID}
          type={size}
        />
      </Col>
    );
  });
  return (
    <Row className="ps-1 pr-1" data-cy={testId || ''}>
      {items}
    </Row>
  );
}
